import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { BarChart3, Users, Eye, Calendar, RefreshCw, TrendingUp, Leaf, Lock, Clock } from 'lucide-react';
import { format, subDays, parseISO, differenceInDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import DomainSelector from '../components/DomainSelector';
import GSCKeywordsWidget from '../components/GSCKeywordsWidget';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Traffic source colors by actual source name
const SOURCE_COLORS = {
  'Google': '#4285f4',
  'Facebook': '#1877f2',
  'Instagram': '#e4405f',
  'Pinterest': '#bd081c',
  'Twitter': '#1da1f2',
  'Bing': '#008373',
  'Yahoo': '#6001d2',
  'Youtube': '#ff0000',
  'Linkedin': '#0077b5',
  'Reddit': '#ff4500',
  'Tiktok': '#000000',
  'Direct': '#6b7280',
  'Default': '#94a3b8'
};

const getSourceColor = (source) => {
  const sourceLower = (source || '').toLowerCase();
  for (const [key, color] of Object.entries(SOURCE_COLORS)) {
    if (sourceLower.includes(key.toLowerCase())) return color;
  }
  return SOURCE_COLORS['Default'];
};

const formatSourceName = (source) => {
  if (!source) return 'Other';
  const s = source.toLowerCase();
  if (s.includes('google')) return 'Google';
  if (s.includes('facebook') || s.includes('fb.')) return 'Facebook';
  if (s.includes('instagram')) return 'Instagram';
  if (s.includes('pinterest')) return 'Pinterest';
  if (s.includes('twitter') || s.includes('t.co')) return 'Twitter';
  if (s.includes('bing')) return 'Bing';
  if (s.includes('yahoo')) return 'Yahoo';
  if (s.includes('youtube')) return 'YouTube';
  if (s.includes('linkedin')) return 'LinkedIn';
  if (s.includes('reddit')) return 'Reddit';
  if (s.includes('tiktok')) return 'TikTok';
  if (s === 'direct' || s === '(direct)') return 'Direct';
  return source.charAt(0).toUpperCase() + source.slice(1);
};

// Map date range days to API string
const dateRangeToDays = (dateRange) => {
  const start = new Date(dateRange.start);
  const end = new Date(dateRange.end);
  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  if (days <= 7) return '7';
  if (days <= 30) return '30';
  if (days <= 90) return '90';
  if (days <= 365) return '365';
  return 'all';
};

const DashboardPage = () => {
  const { token, user } = useAuth();
  const userEmail = user?.email || '';
  const userPlan = user?.subscription?.plan || 'free';
  const isFreePlan = userPlan === 'free';
  
  // Free users can only see 7 days data
  const getDefaultDateRange = () => {
    const maxDays = isFreePlan ? 7 : 30;
    return {
      start: format(subDays(new Date(), maxDays), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    };
  };
  
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);
  const [insights, setInsights] = useState({ evergreen: [], trending: [], traffic_sources: [] });
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const initialLoadDone = useRef(false);
  const pollIntervalRef = useRef(null);

  // Handle domain change
  const handleDomainChange = (domainId) => {
    setSelectedDomain(domainId);
    setSessionsData([]);
    setInsights({ evergreen: [], trending: [], traffic_sources: [] });
    setLastUpdated(null);
    initialLoadDone.current = false;
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Load cached data when domain changes
  useEffect(() => {
    if (!selectedDomain || !token) return;
    
    // Reset to allowed range for free users
    if (isFreePlan) {
      const maxDate = subDays(new Date(), 7);
      const startDate = new Date(dateRange.start);
      if (startDate < maxDate) {
        setDateRange(getDefaultDateRange());
        return;
      }
    }
    
    loadCachedDashboard();
  }, [selectedDomain, token, isFreePlan]);

  // When date range changes, reload from cache
  useEffect(() => {
    if (!initialLoadDone.current || !selectedDomain) return;
    loadCachedDashboard();
  }, [dateRange]);

  // Load cached dashboard data
  const loadCachedDashboard = async () => {
    if (!selectedDomain) return;
    
    const dateRangeStr = dateRangeToDays(dateRange);
    
    try {
      const response = await axios.get(`${API}/cache/dashboard/${selectedDomain}`, {
        params: { date_range: dateRangeStr },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        // Set overview stats (we'll need to fetch full session data separately for charts)
        setInsights({
          evergreen: [],
          trending: data.trending_posts || [],
          traffic_sources: [],
          overview: data.overview
        });
        setLastUpdated(response.data.last_updated);
        setIsRefreshing(response.data.is_refreshing || false);
        initialLoadDone.current = true;
        
        // Start polling if refreshing
        if (response.data.is_refreshing) {
          startPolling(dateRangeStr);
        }
        
        // If cached data exists, also fetch detailed session data for charts
        if (response.data.cached) {
          fetchSessionData();
        }
      } else {
        // No cache, show empty state
        initialLoadDone.current = true;
        if (response.data.message) {
          toast.info(response.data.message);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Please configure your GA4 credentials in the Setup page');
      } else {
        console.error('Failed to load cached dashboard:', error);
      }
      initialLoadDone.current = true;
    }
  };

  // Fetch detailed session data for charts (from existing API)
  const fetchSessionData = async () => {
    if (!token || !selectedDomain) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [sessionsRes, sourcesRes] = await Promise.all([
        axios.post(`${API}/analytics/sessions`, {
          start_date: dateRange.start,
          end_date: dateRange.end,
          config_id: selectedDomain,
        }, { headers }),
        axios.get(`${API}/analytics/traffic-sources`, {
          params: {
            start_date: dateRange.start,
            end_date: dateRange.end,
            config_id: selectedDomain,
          },
          headers
        })
      ]);
      
      setSessionsData(sessionsRes.data.data || []);
      setInsights(prev => ({
        ...prev,
        traffic_sources: sourcesRes.data.traffic_sources || []
      }));
    } catch (error) {
      console.error('Failed to fetch session data:', error);
    }
  };

  // Trigger refresh
  const triggerRefresh = async () => {
    if (!selectedDomain || isRefreshing) return;
    
    setIsRefreshing(true);
    const dateRangeStr = dateRangeToDays(dateRange);
    
    try {
      const response = await axios.post(
        `${API}/cache/refresh/dashboard/${selectedDomain}`,
        { date_range: dateRangeStr },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.info('Refreshing data in background...');
        startPolling(dateRangeStr);
      }
    } catch (error) {
      setIsRefreshing(false);
      toast.error('Failed to refresh: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Poll for refresh completion
  const startPolling = (dateRangeStr) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/cache/dashboard/${selectedDomain}`, {
          params: { date_range: dateRangeStr },
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          if (response.data.data) {
            const data = response.data.data;
            setInsights(prev => ({
              ...prev,
              trending: data.trending_posts || [],
              overview: data.overview
            }));
            setLastUpdated(response.data.last_updated);
          }
          
          if (!response.data.is_refreshing) {
            setIsRefreshing(false);
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            toast.success('Data refreshed successfully!');
            fetchSessionData(); // Fetch updated charts
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000);
  };

  // Format last updated time
  const formatLastUpdated = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const setPresetRange = (days) => {
    // Free users can only select up to 7 days
    if (isFreePlan && days > 7) {
      toast.error('Upgrade to Pro or Lifetime to access more than 7 days of data');
      return;
    }
    setDateRange({
      start: format(subDays(new Date(), days), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    });
  };

  const totalSessions = sessionsData.reduce((sum, item) => sum + (item.sessions || 0), 0);
  const totalUsers = sessionsData.reduce((sum, item) => sum + (item.totalUsers || 0), 0);
  const totalPageViews = sessionsData.reduce((sum, item) => sum + (item.screenPageViews || 0), 0);
  const avgSessionsPerDay = sessionsData.length > 0 ? Math.round(totalSessions / sessionsData.length) : 0;

  // Sort and aggregate chart data based on date range
  const sortedData = [...sessionsData].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  
  // Determine aggregation level based on data points (use const for component scope)
  const dataPoints = sortedData.length;
  let chartData = [];
  
  if (dataPoints > 90) {
    // Aggregate by week for large datasets (90+ days)
    const weeklyData = {};
    sortedData.forEach(item => {
      if (!item.date) return;
      const date = new Date(
        parseInt(item.date.substring(0, 4)),
        parseInt(item.date.substring(4, 6)) - 1,
        parseInt(item.date.substring(6, 8))
      );
      // Get week start (Sunday)
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = format(weekStart, 'yyyy-MM-dd');
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { date: weekStart, sessions: 0, users: 0 };
      }
      weeklyData[weekKey].sessions += item.sessions || 0;
      weeklyData[weekKey].users += item.totalUsers || 0;
    });
    
    chartData = Object.values(weeklyData)
      .sort((a, b) => a.date - b.date)
      .map(item => ({
        date: format(item.date, 'MMM dd'),
        sessions: item.sessions,
        users: item.users,
      }));
  } else if (dataPoints > 31) {
    // Show every 3rd day for medium datasets (31-90 days)
    chartData = sortedData
      .filter((_, index) => index % 3 === 0 || index === sortedData.length - 1)
      .map(item => ({
        date: item.date ? format(new Date(
          parseInt(item.date.substring(0, 4)),
          parseInt(item.date.substring(4, 6)) - 1,
          parseInt(item.date.substring(6, 8))
        ), 'MMM dd') : '',
        sessions: item.sessions || 0,
        users: item.totalUsers || 0,
      }));
  } else {
    // Show all data points for small datasets
    chartData = sortedData.map(item => ({
      date: item.date ? format(new Date(
        parseInt(item.date.substring(0, 4)),
        parseInt(item.date.substring(4, 6)) - 1,
        parseInt(item.date.substring(6, 8))
      ), 'MMM dd') : '',
      sessions: item.sessions || 0,
      users: item.totalUsers || 0,
    }));
  }

  // Prepare pie chart data for traffic sources (already aggregated by backend)
  const pieData = insights.traffic_sources.map(item => ({
    name: item.source || 'Other',
    value: item.sessions || 0,
    color: getSourceColor(item.source || 'Other')
  }));

  const formatAge = (dateStr) => {
    try {
      const days = differenceInDays(new Date(), parseISO(dateStr));
      if (days < 30) return `${days}d ago`;
      if (days < 365) return `${Math.floor(days / 30)}mo ago`;
      return `${Math.floor(days / 365)}y ago`;
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Analytics Dashboard</h1>
          <p className="text-slate-600 mt-1">Overview of your WordPress site traffic</p>
          {/* Last Updated Info */}
          {lastUpdated && (
            <div className="mt-1 text-xs text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>Last updated: {formatLastUpdated(lastUpdated)}</span>
              {isRefreshing && <span className="text-blue-600 ml-2">• Updating in background...</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <DomainSelector onDomainChange={handleDomainChange} />
          <Button
            onClick={triggerRefresh}
            variant="outline"
            disabled={isRefreshing || !selectedDomain}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-slate-700">
          <strong>How to use:</strong> Data is refreshed automatically at 12 AM daily. 
          Click "Refresh" to fetch the latest data manually.
        </p>
      </div>

      {/* Date Range Buttons */}
      <div className="flex gap-2 flex-wrap items-center">
        {[
          { days: 7, label: '7 days' },
          { days: 30, label: '30 days' },
          { days: 90, label: '90 days' },
          { days: 365, label: '12 months' }
        ].map(({ days, label }) => {
          const isRestricted = isFreePlan && days > 7;
          return (
            <Button
              key={days}
              onClick={() => setPresetRange(days)}
              variant={dateRange.start === format(subDays(new Date(), days), 'yyyy-MM-dd') ? 'default' : 'outline'}
              size="sm"
              className={isRestricted ? 'opacity-50' : ''}
              title={isRestricted ? 'Upgrade to unlock' : ''}
            >
              Last {label} {isRestricted && <Lock className="w-3 h-3 ml-1" />}
            </Button>
          );
        })}
        {isFreePlan && (
          <span className="text-xs text-slate-500 ml-2">
            Free plan: 7 days only
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Sessions</p>
                    <p className="text-2xl font-bold">{totalSessions.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500/10 p-2 rounded-lg">
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Users</p>
                    <p className="text-2xl font-bold">{totalUsers.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500/10 p-2 rounded-lg">
                    <Eye className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Page Views</p>
                    <p className="text-2xl font-bold">{totalPageViews.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-500/10 p-2 rounded-lg">
                    <Calendar className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Avg/Day</p>
                    <p className="text-2xl font-bold">{avgSessionsPerDay.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sessions Chart - Takes 2 columns */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Sessions Over Time</CardTitle>
                  {dataPoints > 90 && <span className="text-xs text-slate-400">Aggregated weekly</span>}
                  {dataPoints > 31 && dataPoints <= 90 && <span className="text-xs text-slate-400">Sampled data</span>}
                </div>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#64748b" 
                        style={{ fontSize: '10px' }} 
                        interval={chartData.length > 20 ? Math.floor(chartData.length / 10) : 0}
                        angle={chartData.length > 15 ? -45 : 0}
                        textAnchor={chartData.length > 15 ? "end" : "middle"}
                        height={chartData.length > 15 ? 60 : 30}
                      />
                      <YAxis stroke="#64748b" style={{ fontSize: '11px' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                      <Line type="monotone" dataKey="sessions" stroke="#1e1b4b" strokeWidth={2} dot={chartData.length <= 15 ? { r: 3 } : false} name="Sessions" />
                      <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} dot={chartData.length <= 15 ? { r: 3 } : false} name="Users" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-500 text-center py-10">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Traffic Sources */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Traffic Sources</CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={140}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={55} innerRadius={30}>
                          {pieData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => value.toLocaleString()} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5 mt-3 max-h-[180px] overflow-y-auto">
                      {pieData.map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                            <span className="text-slate-600 truncate">{item.name}</span>
                          </div>
                          <span className="font-medium text-slate-800 ml-2">{item.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500 text-center py-10">No data</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Evergreen & Trending Posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Evergreen Posts */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="bg-green-500/10 p-1.5 rounded">
                    <Leaf className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Evergreen Content</CardTitle>
                    <p className="text-xs text-slate-500">Posts 4+ months old with consistent traffic</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {insights.evergreen.length > 0 ? (
                  <div className="space-y-3">
                    {insights.evergreen.map((post, i) => (
                      <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                        <a href={post.link} target="_blank" rel="noopener noreferrer" 
                           className="text-sm font-medium text-foreground hover:text-primary line-clamp-1">
                          {post.title}
                        </a>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <span className="text-xs text-slate-400">{formatAge(post.date)}</span>
                          <span className="text-xs font-medium text-green-600">{post.sessions.toLocaleString()} sessions</span>
                          {post.consistency && (
                            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                              {post.consistency}% consistent
                            </span>
                          )}
                          {post.months_active && (
                            <span className="text-xs text-slate-500">{post.months_active} months active</span>
                          )}
                        </div>
                        {post.sources?.length > 0 && (
                          <div className="flex gap-1.5 mt-1.5 flex-wrap">
                            {post.sources.map((src, j) => (
                              <span key={j} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-white"
                                    style={{ backgroundColor: getSourceColor(src.name) }}>
                                {formatSourceName(src.name)} {src.sessions}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm text-center py-6">No evergreen posts found</p>
                )}
              </CardContent>
            </Card>

            {/* Trending Posts */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="bg-orange-500/10 p-1.5 rounded">
                    <TrendingUp className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Trending Now</CardTitle>
                    <p className="text-xs text-slate-500">Recent posts performing well</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {insights.trending.length > 0 ? (
                  <div className="space-y-3">
                    {insights.trending.map((post, i) => (
                      <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                        <a href={post.link} target="_blank" rel="noopener noreferrer"
                           className="text-sm font-medium text-foreground hover:text-primary line-clamp-1">
                          {post.title}
                        </a>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-xs text-slate-400">{formatAge(post.date)}</span>
                          <span className="text-xs font-medium text-orange-600">{post.sessions.toLocaleString()} sessions</span>
                        </div>
                        {post.sources?.length > 0 && (
                          <div className="flex gap-1.5 mt-1.5 flex-wrap">
                            {post.sources.map((src, j) => (
                              <span key={j} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-white"
                                    style={{ backgroundColor: getSourceColor(src.name) }}>
                                {formatSourceName(src.name)} {src.sessions}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm text-center py-6">No trending posts found</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* GSC Keywords Widget - Full Width */}
          <div className="mt-6">
            <GSCKeywordsWidget 
              token={localStorage.getItem('token')} 
              configId={selectedDomain} 
              userEmail={userEmail}
              startDate={dateRange.start}
              endDate={dateRange.end}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
